import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';


// layout
import NavigationBar from './components/layout/NavigationBar';
// signin
import Signin from './pages/signin/Signin';
// register
import RegisterForm from './pages/register/RegisterForm';
// create
import CreateOrgUser from "./pages/create/CreateOrgUser";
import CreateNotOrgUser from "./pages/create/CreateNotOrgUser";
import CreateUser from './pages/create/CreateUser';
// admin
import ReadUserList from './pages/admin/ReadUserList';
import PendingAppr from './pages/admin/PendingAppr';
import ReadUserInfo from './pages/admin/ReadUserInfo';
import TestPushList from './pages/admin/TestPushList';
import TestPushCreate from './pages/admin/TestPushCreate';
import TestPushTemplateList from './pages/admin/TestPushTemplateList';
import TestPushTemplateCreate from './pages/admin/TestPushTemplateCreate';
import AdminSettings from './pages/admin/AdminSettings';
// home
import Home from './pages/Home';

// management
import OrganizationInfo from './pages/management/OrganizationInfo';
import ReadEmployeeList from './pages/management/ReadEmployeeList';
import ReadProjectList from './pages/management/ReadProjectList';
// import OrganizationAttendance from './pages/management/OrganizationAttendance'; // [비활성] 출근부 미사용 - 백엔드 AttendanceController 주석처리(2026-06)
// etc
import UpdateUser from './pages/UpdateUser';
import ResumePage from './pages/etc/disable/ResumePage';
import ViewResumePage from './pages/organization/ViewResumePage';
// import Attendance from './pages/Attendance'; // [비활성] 출근부 미사용 - 백엔드 AttendanceController 주석처리(2026-06)
import OrganizationSettings from './pages/management/OrganizationSettings';
// project
import ProjectAssignmentList from './pages/management/ProjectAssignmentList';
import ProjectAssignmentDetail from './pages/project/ProjectAssignmentDetail';
import ProjectManagement from './pages/project/ProjectManagement';
import GanttChartPage from './pages/project/gantt/GanttChartPage';
import SharedGanttPage from './pages/project/gantt/SharedGanttPage';
// push
import PushList from './pages/push/PushList';
import PushCreate from './pages/push/PushCreate';
import PushTemplateList from './pages/push/PushTemplateList';
import PushTemplateCreate from './pages/push/PushTemplateCreate';
// my-info
import MyInfoCheckPw from './pages/myInfo/MyInfoCheckPw';
import MyInfoDetails from './pages/myInfo/MyInfoDetails';
import MyInfoChangePw from './pages/myInfo/MyInfoChangePw';
// error
import Unauthorized from './pages/error/Unauthorized';
// css
import 'bootstrap/dist/css/bootstrap.min.css';

import useTokenStore from './stores/tokens';
import useModalStore from './stores/modal';
import { authApi } from './utils/Intercepter';
import { ToastContainer } from 'react-toastify';
import EmailVerified from './pages/verified/EmailVerified';
import OrganizationJoin from './pages/organization/OrganizationJoin';
import OrganizationAdd from './pages/organization/OrganizationAdd';
import ProjectList from './pages/project/ProjectList';
import ProjectCreate from './pages/project/ProjectCreate';
import ResetPassword from './pages/signin/ResetPassword';
import CalendarPage from './pages/calendar/CalendarPage';
// approval
import ApprovalWritePage from './pages/approval/ApprovalWritePage';
import ApprovalDraftBoxPage from './pages/approval/ApprovalDraftBoxPage';
import ApprovalInboxPage from './pages/approval/ApprovalInboxPage';
// report
import ReportListPage from './pages/report/ReportListPage';
import ReportCreatePickerPage from './pages/report/ReportCreatePickerPage';
import ReportWritePage from './pages/report/ReportWritePage';
import ReportViewPage from './pages/report/ReportViewPage';
// note
import NoteListPage from './pages/note/NoteListPage';
import NoteEditorPage from './pages/note/NoteEditorPage';
import NoteViewPage from './pages/note/NoteViewPage';
// inquiry
import InquiryPage from './pages/inquiry/InquiryPage';
import PrivacyPolicyPage from './pages/etc/PrivacyPolicyPage';

// App 리렌더 시 함수 참조가 바뀌어 하위 라우트 전체가 언마운트되는 문제 방지 — App 외부에 정의
function SignedOutRoute() {
  const location = useLocation();
  const isAuthenticated = useTokenStore((state) => state.isAuthenticated);
  if (isAuthenticated && location.pathname === '/') {
    return <Navigate to='/main' replace />;
  }
  return <Outlet />;
}

function SignedInRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useTokenStore((state) => state.isAuthenticated);
  const isEmailVerified = useTokenStore((state) => state.user?.isEmailVerified);
  const userRole = useTokenStore((state) => state.user?.currentRole);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/', { replace: true });
    } else if (userRole !== 'ADMIN' && !isEmailVerified && location.pathname !== '/verified') {
      navigate('/verified', { replace: true });
    } else if (isEmailVerified && location.pathname === '/verified') {
      navigate('/main', { replace: true });
    }
  }, [isAuthenticated, isEmailVerified, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return <Outlet />;
}

function ProtectedRoute({ allowedRoles }) {
  const userRole = useTokenStore((state) => state.user?.currentRole);
  const hasAccess = allowedRoles.some((role) => userRole === role);
  return hasAccess ? <Outlet /> : <Navigate to="/unauthorized" />;
}


function App() {
  const clearTokens = useTokenStore((state) => state.clearTokens);
  const getRefreshToken = useTokenStore((state) => state.getRefreshToken);
  const user = useTokenStore((state) => state.user);
  const isTokenValid = useTokenStore( state => state.isTokenValid);
  const setUser = useTokenStore((state) => state.setUser);
  const openOrgSelectModal = useModalStore((state) => state.openOrgSelectModal);

  const isRefreshingRef = useRef(false); // 중복 방지 플래그
  const [isInitializing, setIsInitializing] = useState(true); // 초기화 상태 추가

  // 현재 선택된 조직이 없고 소속된 조직 목록이 존재하는 경우 조직 선택 팝업 자동 오픈
  useEffect(() => {
    if (!isInitializing && user && !user.isAdmin && !user.currentOrganizationCode && user.orgUserList?.length > 0) {
      openOrgSelectModal();
    }
  }, [isInitializing, user, openOrgSelectModal]);

  useEffect(() => {
    // 이미 실행 중이면 스킵
    if (isRefreshingRef.current) {
      return;
    }

    const initAuth = async () => {
      isRefreshingRef.current = true;

      const refreshToken = getRefreshToken();
      //console.log('앱 초기화 - RefreshToken 존재:', !!refreshToken);

      if (refreshToken) {
        try {
          if(isTokenValid()){
            //console.log('토큰 유효성 검사 통과');
          }else{
            //console.log('토큰 재발급 시작...');
            await authApi.refreshTokens();
            //console.log('토큰 재발급 성공');
          }
          // DB에서 사용자 조직 목록 가져오기
          const orgsRes = await authApi.getCurrentUserOrgs().catch(() => null);
          if (orgsRes?.data && orgsRes.data.length > 0) {
            const orgList = orgsRes.data;
            const savedOrgCode = localStorage.getItem('currentOrganizationCode');
            const savedOrgName = localStorage.getItem('currentOrganizationName');

            let restoreData = { orgUserList: orgList };

            if (savedOrgCode && savedOrgName) {
              const isValid = orgList.some(org => org.organizationCode === savedOrgCode);
              if (isValid) {
                const activeOrg = orgList.find(org => org.organizationCode === savedOrgCode);
                restoreData = {
                  ...restoreData,
                  currentOrganizationCode: savedOrgCode,
                  currentOrganizationName: savedOrgName,
                  currentRole: activeOrg?.userRole,
                  currentOrganizationApproved: activeOrg?.isApproved ?? activeOrg?.approved
                };
                console.log('localStorage에서 조직 복원:', savedOrgCode, savedOrgName);
              } else {
                console.log('저장된 조직이 유효하지 않음, localStorage 제거');
                localStorage.removeItem('currentOrganizationCode');
                localStorage.removeItem('currentOrganizationName');
              }
            }
            setUser(restoreData);
          } else if (orgsRes?.data) {
            setUser({ orgUserList: orgsRes.data });
          }
        } catch (error) {
          console.error('토큰 갱신 및 조직 목록 조회 실패:', error);
        }
      } else {
        console.log('RefreshToken 없음 - 로그인 필요');
      }
      setIsInitializing(false);
    };

    initAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    // Token store 정리 (AccessToken, RefreshToken)
    clearTokens();
    window.location.href = '/';
  };


  if (isInitializing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        로딩 중...
      </div>
    );
  }

  return (
    <Router>
      <ToastContainer />
      <NavigationBar onLogout={handleLogout}/>
      <Routes>
        {/* 로그인 이전 페이지 */}
        <Route element={<SignedOutRoute />}>
          <Route index element={<Signin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* 회원 유형 선택 */}
          <Route path="/register" element={<RegisterForm />} />
          {/* 회원 가입 화면*/}
          <Route path="/create/user" element={<CreateUser />} />
          <Route path="/create/orgUser" element={<CreateOrgUser />} />
          <Route path="/create/notOrgUser" element={<CreateNotOrgUser />} />

        </Route>
        {/* 로그인 이후 페이지 */}
        <Route element={<SignedInRoute />}>
          {/* 메인 홈 */}
          <Route path="/main" element={<Home/>} />
          {/* 이메일 인증 */}
          <Route path="/verified" element={<EmailVerified/>} />
          {/* 어드민 메뉴 */}
          <Route element={<ProtectedRoute allowedRoles={['ADMIN']} />}>
            <Route path="/admin/pendingAppr" element={<PendingAppr />} />
            <Route path="/admin/readUserList" element={<ReadUserList />} />
            <Route path="/admin/readUserInfo" element={<ReadUserInfo />} />

            {/* 푸시 관리 */}
            <Route path="/admin/push/list" element={<TestPushList />} /> {/* 푸시 목록 */}
            <Route path="/admin/push/create" element={<TestPushCreate />} /> {/* 푸시 생성 */}
            <Route path="/admin/push/edit" element={<TestPushCreate />} /> {/* 푸시 수정 */}
            <Route path="/admin/push/template/list" element={<TestPushTemplateList />} /> {/* 템플릿 목록 */}
            <Route path="/admin/push/template/create" element={<TestPushTemplateCreate />} /> {/* 템플릿 생성 */}
            <Route path="/admin/push/template/edit" element={<TestPushTemplateCreate />} /> {/* 템플릿 수정 */}
            <Route path="/admin/settings" element={<AdminSettings />} /> {/* 어드민 설정 */}
          </Route>

          {/* 바로가기 및 출석 */}
          {/* [비활성] 출근부 미사용 - 백엔드 AttendanceController 주석처리(2026-06). 재활성 시 백엔드 스코프 적용 + import 복구 */}
          {/* <Route path="/attendance" element={<Attendance />} /> */}

          {/* 조직 관리 */}
          <Route path="/organization/info" element={<OrganizationInfo/>} />   {/* 조직 정보 */}
          <Route path="/organization/projectAssignmentList" element={<ProjectAssignmentList />} />   {/* 인력 투입 정보 */}
          {/* [비활성] 출근부 미사용 - 백엔드 AttendanceController 주석처리(2026-06) */}
          {/* <Route path="/organization/attendance" element={<OrganizationAttendance />} /> 조직 출석 */}
          <Route path="/organization/join" element={<OrganizationJoin />} /> {/* 조직 가입 */}
          <Route path="/organization/orgAdd" element={<OrganizationAdd />} /> {/* 조직 추가 */}
          <Route path="/organization/settings" element={<OrganizationSettings />} /> {/* 조직 설정(직급·부서) */}
          <Route path="/employee/list" element={<ReadEmployeeList />} />  {/* 직원 목록 ( 회원 목록 ) */}

          {/* 프로젝트 관리 */}
          <Route path="/project/readProjectList" element={<ReadProjectList />} />    {/* 프로젝트 목록 */}
          <Route path="/project/list" element={<ProjectList />} />      {/* 프로젝트 목록 */}
          <Route path="/project/create" element={<ProjectCreate />} />  {/* 프로젝트 생성 */}
          <Route path="/project/edit" element={<ProjectCreate />} />    {/* 프로젝트 수정 */}
          <Route path="/gantt-chart" element={<GanttChartPage />} />    {/* 간트 차트 */}

          <Route path="/project/management" element={<ProjectManagement />} /> {/* 프로젝트 관리 / 삭제 예정*/}
          <Route path="/project/assignments/:projectId" element={<ProjectAssignmentDetail />} /> {/* 프로젝트 상세보기 / 삭제 예정*/}

          {/* 일정 */}
          <Route path="/calendar" element={<CalendarPage />} /> {/* 일정 관리 */}

          {/* 전자결재 */}
          <Route path="/approval/write" element={<ApprovalWritePage />} /> {/* 기안 작성 */}
          <Route path="/approval/draft-box" element={<ApprovalDraftBoxPage />} /> {/* 기안함 */}
          <Route path="/approval/inbox" element={<ApprovalInboxPage />} /> {/* 결재함 */}

          <Route path="/report" element={<ReportListPage />} /> {/* 보고서 목록 */}
          <Route path="/report/create" element={<ReportCreatePickerPage />} /> {/* 보고서 양식 선택 */}
          <Route path="/report/write" element={<ReportWritePage />} /> {/* 보고서 작성 */}
          <Route path="/report/:id" element={<ReportViewPage />} /> {/* 보고서 상세 */}

          <Route path="/note" element={<NoteListPage />} /> {/* 노트 목록 */}
          <Route path="/note/create" element={<NoteEditorPage />} /> {/* 노트 작성 */}
          <Route path="/note/:id" element={<NoteViewPage />} /> {/* 노트 상세 */}
          <Route path="/note/:id/edit" element={<NoteEditorPage />} /> {/* 노트 수정 */}

          {/* 푸시 관리 */}
          <Route path="/push/list" element={<PushList />} /> {/* 푸시 목록 */}
          <Route path="/push/create" element={<PushCreate />} /> {/* 푸시 생성 */}
          <Route path="/push/edit" element={<PushCreate />} /> {/* 푸시 수정 */}
          <Route path="/push/template/list" element={<PushTemplateList />} /> {/* 템플릿 목록 */}
          <Route path="/push/template/create" element={<PushTemplateCreate />} /> {/* 템플릿 생성 */}
          <Route path="/push/template/edit" element={<PushTemplateCreate />} /> {/* 템플릿 수정 */}

          {/* 마이페이지 */}
          <Route path="/my-info/check-pw" element={<MyInfoCheckPw />} /> {/* 비밀번호 확인 */}
          <Route path="/my-info/details" element={<MyInfoDetails />} /> {/* 상세정보 확인 */}
          <Route path="/my-info/change-pw" element={<MyInfoChangePw />} /> {/* 비밀번호 변경 */}

          {/* 분류 중 */}
          <Route path="/updateUser" element={<UpdateUser />} />
          <Route path="/resumePage" element={<ResumePage />} />
          <Route path="/viewResumePage" element={<ViewResumePage />} />
          {/* 사용 확인 안됨  */}
          {/* <Route path="/addOrganizationUser" element={<AddOrganizationUser />} /> */}
          {/* <Route path="/readOrganizationList" element={<ReadOrganizationList />} /> */}
          {/* <Route path="/createOrganization" element={<CreateOrganization />} /> */}
        </Route>
        {/* 공개 접근 페이지 (로그인 불필요) */}
        <Route path="/inquiry" element={<InquiryPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/gantt/shared/:shareId" element={<SharedGanttPage />} />
        {/* 에러 페이지 */}
        <Route path="/error/unauthorized" element={<Unauthorized />} />
      </Routes>
    </Router>
  );
}

export default App;
